import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Circle, ExternalLink, FileText, Image, Loader2, RotateCcw, Sparkles, Upload, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useOutletContext, useParams } from 'react-router'

import { alta, useEstimacionDelAlta, useProgresoDeAlta } from '../../api/alta'
import { diseno, useEstadoDelDiseno } from '../../api/consultas'
import { importacion, type Informe } from '../../api/importacion'
import type { ErrorDeLaApi } from '../../api/nucleo'
import type { PasoDeAlta } from '../../api/tipos'
import type { Contexto } from '../Modulo'
import { Aviso, Boton, Cargando, Tarjeta, Titulo } from '../../componentes/ui'
import { EstructuraEnElAlta } from '../../estructura-ia/EnElAlta'
import { DialogoDePublicacion } from '../../publicar/DialogoDePublicacion'
import { ResumenDelInforme, SeguimientoDeImportacion } from '../Importar'
import { lineasDeEstimacion, textoDeEstimacion } from '../consumo'
import { avisosDe, faltaLaPortada } from '../primeraPublicacion'
import { continuarConLoSubido } from './continuar'
import { conEnlaceAImportar, lineaDeProductos } from './productosDelAlta'


const ROTULOS: Record<PasoDeAlta, string> = {
  sitio: 'Crear la web',
  ajustes: 'Guardar la marca y el contacto',
  medios: 'Guardar las imágenes',
  tema: 'Aplicar la plantilla',
  formulario: 'Crear el formulario de contacto',
  paginas: 'Crear las páginas con su estructura',
  redactar: 'Redactar los textos a partir de tu brief',
  estructura: 'Organizar tu web: tipos de contenido, campos y fichas',
  legales: 'Redactar los textos legales con tus datos',
  productos: 'Importar tus productos y sus categorías',
  menus: 'Montar los menús',
  diseno: 'Afinar el diseño a tu marca con IA',
  construir: 'Construir la previa',
  aviso: 'Avisarte por correo',
}

const CONSTRUIDA = ['lista', 'publicada', 'archivada']

/**
 * Una pestaña abierta **en el gesto del clic**, para llevarla a la previa cuando termine lo
 * que va antes (publicar las páginas puede tardar unos segundos): abrirla después, fuera del
 * clic, la bloquearía el navegador. Si no se llega a la previa, se cierra.
 *
 * Si el navegador la bloqueó (o alguien la cerró), `ir` no lo vuelve a intentar —fuera del
 * clic también se bloquearía, en silencio— y dice que no ha podido: la pantalla enseña
 * entonces un enlace para abrirla a mano.
 */
const pestanaParaLaPrevia = () => {
  const ventana = window.open('about:blank', '_blank')
  if (ventana) {
    ventana.document.title = 'Preparando la previa…'
    ventana.document.body.textContent = 'Publicando tus páginas y preparando la previa…'
  }
  return {
    ir: (url: string): boolean => {
      if (!ventana || ventana.closed) return false
      // Que la previa no pueda tocar esta pestaña (`window.opener`), como con `noopener`.
      ventana.opener = null
      ventana.location.href = url
      return true
    },
    cerrar: () => ventana?.close(),
  }
}

type Mensaje = {
  texto: string
  tono: 'aviso' | 'error' | 'ok'
  /** La previa, cuando el navegador no ha dejado abrirla sola. */
  previa?: string
}

/**
 * El progreso del alta (docs/alta-de-web-con-plantillas.md §8): los pasos, uno a uno,
 * y al terminar qué hacer: ver la previa, revisar el contenido, publicar. Si un paso
 * falla, la web se queda a medias pero usable, y aquí se dice y se ofrece reintentar.
 */
export const Progreso = () => {
  const { espacio, permisos } = useOutletContext<Contexto>()
  const { sitio: enLaRuta } = useParams()
  const sitio = Number(enLaRuta)
  // `importacion`: el trabajo que puso en cola el asistente. `informe`: el de antes de la cola, si queda en el historial del navegador.
  const { state } = useLocation() as { state?: { aviso?: null | string; avisos?: string[]; importacion?: null | number; informe?: Informe | null } }
  const clienteDeConsultas = useQueryClient()
  const { data, isPending, error, refetch } = useProgresoDeAlta(sitio)
  // Lo que no salió al crear la web (una imagen que no subió, un documento que no se leyó): un aviso por línea.
  const avisos = avisosDe(state)
  const [mensaje, setMensaje] = useState<Mensaje | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const trabajoDeProductos = typeof state?.importacion === 'number' ? state.importacion : null
  const informe = trabajoDeProductos === null ? (state?.informe ?? null) : null
  const [publicados, setPublicados] = useState<number[]>([])
  // La importación de los productos del Excel: la lanza el propio alta en su paso (antes, el asistente la dejaba en el historial del navegador).
  const importacionDelAlta = data?.productos?.importacion?.id ?? trabajoDeProductos
  // El contenido publicado de la web: sin su portada, la previa sale en «Esta página no existe».
  const { data: estadoDelDiseno } = useEstadoDelDiseno(sitio)
  const contenido = estadoDelDiseno?.contenido ?? null
  // «Publicar mi web» (el mismo diálogo que la cabecera y Diseño): `todo`, con el diseño; `previa`, solo el contenido, para poder verlo en la previa.
  const [dialogo, setDialogo] = useState<null | 'previa' | 'todo'>(null)
  // Toda llamada a la IA cobra (plan del CMS dinámico §9): antes de continuar se dice cuánto cuesta y si llega.
  const { data: estimacion, error: errorDeEstimacion } = useEstimacionDelAlta(sitio, data?.alta?.estado === 'materiales' && permisos.esDueno)

  // Cuando termina, la tarjeta de «Mis sitios web» tiene que enterarse.
  const estado = data?.alta?.estado
  useEffect(() => {
    if (estado === 'terminada' || estado === 'fallida') {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['sitios', espacio] })
      // Y lo que cuenta el estado del diseño: las páginas acaban de nacer.
      void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado', sitio] })
    }
  }, [estado, espacio, sitio, clienteDeConsultas])

  if (!Number.isInteger(sitio)) return <Aviso tono="error">Esa dirección no lleva a ninguna web.</Aviso>
  if (error) return <Aviso tono="error">{(error as ErrorDeLaApi).message}</Aviso>
  if (isPending || !data) return <Cargando que="Leyendo el progreso" />
  if (!data.alta) return <Aviso tono="info">Esta web no se creó con el asistente: no hay progreso que enseñar.</Aviso>

  const { alta: a, version, faltan } = data
  const base = `/e/${espacio}/s/${sitio}`
  const construida = version !== null && CONSTRUIDA.includes(version.estado)
  const construyendo = version !== null && (version.estado === 'en-cola' || version.estado === 'construyendo')
  // Reintentar el alta, seguir con los materiales e importar son del dueño de la cuenta, como crear la web.
  const propietario = permisos.esDueno
  // Un paso opcional (el diseño) puede fallar sin parar el alta: la web está, y ese paso se repite solo.
  const hayFallidos = a.pasos.some((p) => p.estado === 'fallido')
  // Lleva un buen rato sin dar señales (el servidor se reinició a mitad): se ofrece reintentarla.
  const atascada = data.atascada === true

  const accion = async (hacer: () => Promise<unknown>) => {
    setOcupado(true)
    setMensaje(null)
    try {
      await hacer()
      await refetch()
    } catch (e) {
      setMensaje({ texto: (e as ErrorDeLaApi).message || 'Algo no ha ido bien.', tono: 'error' })
    } finally {
      setOcupado(false)
    }
  }
  // La pestaña se abre en el gesto del clic y se lleva a la previa cuando llega su enlace:
  // abierta después de un `await`, Safari en un iPhone no la abre.
  const abrirPrevia = () => {
    const pestana = pestanaParaLaPrevia()
    void accion(async () => {
      try {
        const url = (await diseno(sitio).previa(version!.numero)).url
        if (!pestana.ir(url)) setMensaje({ texto: 'La previa está lista.', tono: 'ok', previa: url })
      } catch (fallo) {
        pestana.cerrar()
        throw fallo
      }
    })
  }
  // La previa enseña lo publicado (§13): con la portada en borrador saldría «Esta página no
  // existe». En vez de abrirla vacía, se ofrece publicar el contenido (y al terminar, verla).
  const faltaPortada = faltaLaPortada(contenido)
  const verLaPrevia = () => {
    if (faltaPortada) return setDialogo('previa')
    abrirPrevia()
  }

  const titulo = a.esperandoPlan
    ? 'Revisa cómo se organiza tu web'
    : atascada
    ? 'Parece que se ha atascado'
    : a.estado === 'terminada'
      ? construida
        ? 'Tu web está lista para revisar'
        : 'Tu web está montada'
      : a.estado === 'fallida'
        ? 'El alta se ha quedado a medias'
        : a.estado === 'materiales'
          ? 'La web espera tus materiales'
          : 'Creando tu web'

  return (
    <>
      <Titulo
        descripcion={
          a.esperandoPlan
            ? 'El alta está esperando a que revises la propuesta de abajo. Cuando la apliques, o sigas con la plantilla, continuará sola.'
            : atascada
            ? 'Lleva un buen rato sin avanzar: probablemente el servidor se reinició a mitad. Reintentar lo retoma desde el paso en que se quedó; lo que ya se creó no se duplica.'
            : a.estado === 'terminada'
              ? 'Las páginas han nacido como borradores con su estructura y sus textos, y el diseño va afinado a tu marca. Revísalo todo, escribe lo que falte y publica cuando estés a gusto.'
              : a.estado === 'fallida'
                ? 'La web existe y se puede usar; lo que falló se puede volver a intentar.'
                : a.estado === 'materiales'
                  ? 'La web se creó, pero el alta no ha seguido: faltan imágenes, decidir qué hacer con un documento que no se pudo leer, o créditos para la IA. Puedes continuar sin ellos.'
                  : `Un momento: se está aplicando la plantilla, creando las páginas${a.tipo === 'tienda' ? ', importando tus productos' : ''} y montando los menús.`
        }
        rotulo={data.nombre}
      >
        {titulo}
      </Titulo>

      {a.esperandoPlan && <EstructuraEnElAlta alResolver={() => void refetch()} plan={a.esperandoPlan} puede={propietario} />}

      {avisos.length > 0 && (
        <div className="mb-4">
          <Aviso tono="aviso">
            {avisos.map((aviso) => (
              <span className="mt-1 block first:mt-0" key={aviso}>
                {aviso}
              </span>
            ))}
          </Aviso>
        </div>
      )}
      {mensaje && (
        <div className="mb-4">
          <Aviso tono={mensaje.tono}>
            {mensaje.texto}
            {mensaje.previa && (
              <>
                {' '}
                Tu navegador no ha dejado abrir la previa sola:{' '}
                <a className="font-medium underline" href={mensaje.previa} rel="noopener noreferrer" target="_blank">
                  Abrir la previa
                </a>
              </>
            )}
          </Aviso>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Tarjeta>
          <ol className="flex flex-col gap-3">
            {a.pasos.map((p) => {
              const icono =
                p.estado === 'hecho' ? (
                  <CheckCircle2 className="size-5 text-ok" />
                ) : p.estado === 'fallido' ? (
                  <XCircle className="size-5 text-error" />
                ) : p.estado === 'omitido' ? (
                  <Circle className="size-5 text-tenue" />
                ) : (a.estado === 'en-curso' || a.estado === 'en-cola') && !atascada && !a.esperandoPlan ? (
                  <Loader2 className="size-5 animate-spin text-violeta" />
                ) : (
                  <Circle className="size-5 text-linea-fuerte" />
                )
              const deProductos = p.clave === 'productos' ? lineaDeProductos(p, data.productos) : null
              return (
                <li className="flex items-start gap-3" key={p.clave}>
                  <span className="mt-0.5 shrink-0">{icono}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {ROTULOS[p.clave] ?? p.clave}
                      {p.estado === 'omitido' && !p.mensaje && <span className="ml-2 text-xs font-normal text-tenue">(no hacía falta)</span>}
                      {a.esperandoPlan && p.clave === 'estructura' && p.estado !== 'hecho' && <span className="ml-2 text-xs font-normal text-violeta">(esperando a que lo revises)</span>}
                    </p>
                    {p.mensaje && <p className={`mt-0.5 text-[13px] ${p.estado === 'fallido' ? 'text-error' : 'text-tenue'}`}>{p.mensaje}</p>}
                    {deProductos && (
                      <p className="mt-0.5 text-[13px] text-tenue" role="status">
                        {deProductos}
                      </p>
                    )}
                    {conEnlaceAImportar(p) && propietario && (
                      <Link className="mt-0.5 inline-block text-[13px] font-medium text-violeta hover:underline" to={`${base}/productos/importar`}>
                        Importar desde Productos
                      </Link>
                    )}
                    {p.clave === 'construir' && p.estado === 'hecho' && version && (
                      <p className="mt-0.5 text-[13px] text-tenue">
                        {construyendo ? 'Construyendo la previa: tarda un par de minutos.' : construida ? `Versión ${version.numero} construida.` : version.estado === 'fallida' ? 'La construcción falló. Desde Diseño se puede volver a construir.' : `Versión ${version.numero}: ${version.estado}.`}
                      </p>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        </Tarjeta>

        <div className="flex flex-col gap-3">
          {a.estado === 'terminada' && faltan.length > 0 && (
            <Tarjeta>
              <h2 className="font-display text-base font-semibold">Textos que te toca escribir</h2>
              <p className="mt-1 text-[13px] text-tenue">Están marcados «por rellenar» en sus páginas; escríbelos tú.</p>
              <ul className="mt-3 flex flex-col gap-2">
                {faltan.slice(0, 12).map((f) => (
                  <li className="text-[13px]" key={f.clave}>
                    <span className="font-medium">{f.rotulo}</span>
                    <span className="text-tenue"> — {f.nota}</span>
                  </li>
                ))}
                {faltan.length > 12 && <li className="text-xs text-tenue">y {faltan.length - 12} más</li>}
              </ul>
            </Tarjeta>
          )}
          {a.estado === 'terminada' && (
            <Tarjeta>
              <h2 className="font-display text-base font-semibold">Qué hacer ahora</h2>
              <div className="mt-3 flex flex-col gap-2">
                {permisos.tiene('contenido.publicar') && (
                  <Boton disabled={construyendo} icono={<Upload />} onClick={() => setDialogo('todo')} tipo="primario">
                    Publicar mi web
                  </Boton>
                )}
                <Boton cargando={ocupado || construyendo} disabled={!construida} icono={<ExternalLink />} onClick={verLaPrevia}>
                  {construyendo ? 'Preparando la previa…' : 'Ver la previa'}
                </Boton>
                <Link to={`${base}/paginas`}>
                  <Boton className="w-full" icono={<FileText />}>
                    Revisar el contenido
                  </Boton>
                </Link>
                <Link to={`${base}/medios`}>
                  <Boton className="w-full" icono={<Image />}>
                    Subir más imágenes
                  </Boton>
                </Link>
                <Link to={`${base}/diseno`}>
                  <Boton className="w-full" icono={<Sparkles />}>
                    Diseño y publicación
                  </Boton>
                </Link>
              </div>
              <p className="mt-3 text-xs text-tenue">Publicar es un acto tuyo: tu web no sale a internet hasta que publiques el diseño, y lo que publiques antes solo se ve en la previa. «Publicar mi web» lo hace todo de una vez, en ese orden.</p>
            </Tarjeta>
          )}
          {dialogo && (
            <DialogoDePublicacion
              cerrar={() => setDialogo(null)}
              conDiseno={dialogo === 'todo'}
              espacio={espacio}
              explicacion={dialogo === 'previa' ? 'La previa enseña lo publicado, y tus páginas están en borrador: por eso saldría «Esta página no existe». Publícalas para verlas; el diseño, cuando quieras.' : undefined}
              sitio={sitio}
            />
          )}
          {(a.estado === 'fallida' || (a.estado === 'terminada' && hayFallidos) || atascada) && propietario && (
            <Tarjeta>
              <h2 className="font-display text-base font-semibold">Volver a intentar</h2>
              <p className="mt-1 text-[13px] text-tenue">
                {atascada
                  ? 'Se retoma desde el paso en que se quedó; lo que ya se creó no se duplica.'
                  : a.estado === 'terminada'
                    ? 'La web está montada; se repite solo lo que falló, y si sale una versión nueva se vuelve a construir la previa.'
                    : 'Se repite desde el paso que falló; lo que ya se creó no se duplica.'}
              </p>
              <Boton cargando={ocupado} className="mt-3" icono={<RotateCcw />} onClick={() => void accion(() => alta.reintentar(sitio))} tipo="primario">
                Reintentar
              </Boton>
            </Tarjeta>
          )}
          {a.estado === 'materiales' && propietario && (
            <Tarjeta>
              <h2 className="font-display text-base font-semibold">Continuar con lo que hay</h2>
              <p className="mt-1 text-[13px] text-tenue">La web se monta con lo que ya se subió: las imágenes que llegaron a subirse se colocan en los huecos de la plantilla, las que falten se suben después desde Medios, y con lo que no se haya leído no se redacta nada.</p>
              {estimacion ? (
                <div className="mt-3">
                  <Aviso tono={estimacion.alcanza ? 'info' : 'aviso'}>{textoDeEstimacion(estimacion)}</Aviso>
                  {estimacion.pasos.length > 0 && (
                    <ul className="mt-2 flex flex-col gap-0.5 text-[13px] text-tinta-2">
                      {lineasDeEstimacion(estimacion).map((linea) => (
                        <li key={linea}>{linea}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : errorDeEstimacion ? (
                <p className="mt-3 text-[13px] text-tenue">No hemos podido calcular lo que costará la IA; si no te llegan los créditos, te lo diremos antes de empezar.</p>
              ) : (
                <p className="mt-3 text-[13px] text-tenue">Calculando lo que costará la IA…</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {estimacion && !estimacion.alcanza ? (
                  <>
                    <Link className="inline-flex h-9 shrink-0 items-center justify-center rounded-boton bg-violeta px-3.5 text-sm font-medium whitespace-nowrap text-white shadow-suave hover:bg-violeta-2" to={`/e/${espacio}/creditos`}>
                      Conseguir créditos
                    </Link>
                    <Boton cargando={ocupado} onClick={() => void accion(() => continuarConLoSubido(espacio, sitio, { sinIA: true }))}>
                      Seguir sin IA
                    </Boton>
                  </>
                ) : (
                  <>
                    <Boton cargando={ocupado} onClick={() => void accion(() => continuarConLoSubido(espacio, sitio))} tipo="primario">
                      Continuar el alta
                    </Boton>
                    {estimacion && estimacion.total > 0 && (
                      <Boton disabled={ocupado} onClick={() => void accion(() => continuarConLoSubido(espacio, sitio, { sinIA: true }))} tipo="discreto">
                        Seguir sin IA
                      </Boton>
                    )}
                  </>
                )}
              </div>
            </Tarjeta>
          )}
        </div>
      </div>

      {(importacionDelAlta !== null || informe) && (
        <div className="mt-4">
          <Tarjeta>
            <h2 className="font-display text-base font-semibold">Productos importados</h2>
            <div className="mt-3">
              {importacionDelAlta !== null ? (
                <SeguimientoDeImportacion espacio={espacio} id={importacionDelAlta} sitio={sitio} />
              ) : (
                <ResumenDelInforme
                  alPublicar={(ids) =>
                    void accion(async () => {
                      const r = await importacion.publicar(sitio, ids)
                      setPublicados((p) => [...p, ...r.publicados])
                      if (r.fallidos.length > 0) setMensaje({ texto: `${r.fallidos.length} no se pudieron publicar: ${r.fallidos.map((f) => f.motivo).join(' · ')}`, tono: 'aviso' })
                    })
                  }
                  espacio={espacio}
                  informe={informe!}
                  publicados={publicados}
                  publicando={ocupado}
                  sitio={sitio}
                />
              )}
            </div>
            <Link className="mt-3 inline-block text-[13px] font-medium text-violeta hover:underline" to={`${base}/productos`}>
              Ver los productos
            </Link>
          </Tarjeta>
        </div>
      )}
    </>
  )
}
